import { AgGridReact } from '@ag-grid-community/react';
import { useAgGrid } from '@tassat/ag-grid';
import { isEqual } from 'lodash';
import React from 'react';
import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-material.css';

import { AG_GRID_SMALL_HEADER_HEIGHT } from 'Lib/constant';
import ActionButtonsCellRenderer from 'SharedComponents/cellRenderComponent/actionButtonsCellRenderer';
import UserAccountRoleCellRenderer from 'SharedComponents/cellRenderComponent/userAccountRoleCellRenderer';
import UserNameEmailCellRenderer from 'SharedComponents/cellRenderComponent/userNameEmailCellRenderer';
import UserStatusCellRenderer from 'SharedComponents/cellRenderComponent/userStatusCellRenderer';
import { useFeatures } from 'SharedComponents/hooks/useFeatures';

import { firmWalletUsersColumnDefs, readonlyFirmWalletUsersColumnDefs } from './gridOptions';

import type { FirmWalletUsersGridProps } from '.';

import 'SharedComponents/agGrid/styles/agGridOverrides.css';

export function FirmWalletUsersAgGrid (props: FirmWalletUsersGridProps) {
    const {
        firm,
        wallet,
        userAccounts,
        userRole
    } = props;

    const [features] = useFeatures();
    const [ rowData, setRowData ] = React.useState(userAccounts);
    const [ gridApi, onGridReady ] = useAgGrid();
    const shownRows = gridApi && gridApi.api.getRenderedNodes();

    React.useEffect(() => {
        if (rowData && !isEqual(rowData, userAccounts)) {
            setRowData(userAccounts);
        }
    }, [ userAccounts, rowData ]);

    const columnDefs = React.useMemo(() => {
        const editable = userRole.IsManageFirmAllowed && !wallet.IsClosed && !firm?.IsClosed;

        return editable ?
            firmWalletUsersColumnDefs({ FirmId: firm.FirmId }, features, wallet)
            : readonlyFirmWalletUsersColumnDefs({ FirmId: firm.FirmId }, features);
    }, [
        firm?.FirmId,
        firm?.IsClosed,
        wallet,
        features,
        userRole?.IsManageFirmAllowed
    ]);

    React.useEffect(() => {
        refreshHeaders();
    }, [ rowData, shownRows ]);

    const refreshHeaders = () => {
        if (gridApi) {
            gridApi.api.refreshHeader();
        }
    };

    return (
        <div
            className='ag-theme-material'
            id='firmWalletUsersGrid'
            style={{ height: '100%' }}
        >
            <AgGridReact
                animateRows={false}
                columnDefs={columnDefs}
                columnMenu='legacy'
                components={{
                    userCellRenderer: UserNameEmailCellRenderer,
                    userStatusCellRenderer: UserStatusCellRenderer,
                    userAccountRoleRenderer: UserAccountRoleCellRenderer,
                    actionButtonsCellRenderer: ActionButtonsCellRenderer
                }}
                context={{ features }}
                defaultColDef={{
                    resizable: true,
                    filter: true,
                    sortable: true
                }}
                enableCellTextSelection={true}
                getRowId={({ data }) => data.userAccount.UserAccountId}
                headerHeight={AG_GRID_SMALL_HEADER_HEIGHT}
                onFilterChanged={refreshHeaders}
                onGridReady={onGridReady}
                rowData={rowData}
                suppressCellFocus={true}
                suppressContextMenu={true}
            />
        </div>
    );
}